/**
 * Utility สำหรับดึงค่าปกติจาก medium.json ผ่าน API
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * ดึงค่าปกติจาก medium.json ผ่าน NormalRangeController API
 * @param {string} testName - ชื่อการตรวจ เช่น "Hemoglobin", "WBC"
 * @param {string} sex - เพศ "M" (ชาย) หรือ "F" (หญิง)
 * @param {number} age - อายุ (รองรับทศนิยม เช่น 0.5 = 6 เดือน)
 * @returns {Promise<object|null>} - {normal_range: string, unit: string, category: string} หรือ null ถ้าไม่พบ
 */
export const getNormalRange = async (testName, sex, age) => {
  try {
    // แปลง sex เป็นรูปแบบที่ API ต้องการ (M/F)
    if (sex === 'male' || sex === '1') sex = 'M';
    if (sex === 'female' || sex === '0') sex = 'F';

    const response = await fetch(
      `${API_BASE_URL}/api/normal-range?test=${encodeURIComponent(testName)}&sex=${sex}&age=${age}`
    );

    if (!response.ok) {
      console.warn(`Failed to fetch normal range for ${testName}`);
      return null;
    }

    const data = await response.json();

    if (data.status === 'success' && data.data) {
      return {
        normal_range: data.data.normal_range,
        unit: data.data.unit || '',
        category: data.data.category || ''
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching normal range:', error);
    return null;
  }
};

/**
 * ดึงค่าปกติหลายรายการพร้อมกัน
 * @param {Array} tests - [{name: string, sex: string, age: number}]
 * @returns {Promise<Array>} - Array of results
 */
export const getNormalRanges = async (tests) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/normal-range/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tests })
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.status === 'success' ? data.data : [];
  } catch (error) {
    console.error('Error fetching normal ranges:', error);
    return [];
  }
};

/**
 * แปลง normal_range string เป็น min และ max values
 * @param {string} rangeStr - เช่น "12-16", "<5.7", ">90", "0.55-1.02"
 * @returns {object} - {min: number|null, max: number|null}
 */
export const parseNormalRange = (rangeStr) => {
  if (!rangeStr || rangeStr === '-' || rangeStr === 'xxx') {
    return { min: null, max: null };
  }

  // กรณี: "12-16" หรือ "0.55-1.02"
  const rangeMatch = rangeStr.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)$/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // กรณี: "<5.7" หรือ "<200"
  const lessThanMatch = rangeStr.match(/^<\s*(\d+\.?\d*)$/);
  if (lessThanMatch) {
    return {
      min: 0,
      max: parseFloat(lessThanMatch[1])
    };
  }

  // กรณี: ">90" หรือ ">50"
  const greaterThanMatch = rangeStr.match(/^>\s*(\d+\.?\d*)$/);
  if (greaterThanMatch) {
    return {
      min: parseFloat(greaterThanMatch[1]),
      max: Infinity
    };
  }

  // กรณี: "0-5,1-2,2-3" (เช่น เซลล์เยื่อบุผิว) - ใช้ช่วงแรก
  const multiRangeMatch = rangeStr.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
  if (multiRangeMatch) {
    return {
      min: parseFloat(multiRangeMatch[1]),
      max: parseFloat(multiRangeMatch[2])
    };
  }

  // กรณีอื่นๆ: "Negative", "Clear", "เหลืองอ่อน" - ไม่สามารถแปลงเป็นตัวเลขได้
  return { min: null, max: null };
};

/**
 * เปรียบเทียบค่าแลปกับค่าปกติและคืนค่าสถานะพร้อมสี
 * @param {number|string} value - ค่าที่ได้จากการตรวจ
 * @param {string} normalRange - ค่าปกติจาก medium.json เช่น "12-16", "<5.7"
 * @param {string} testName - ชื่อการตรวจ (สำหรับ display)
 * @returns {object} - {title: string, color: string, caption: string, suggest: string}
 */
export const compareWithNormalRange = (value, normalRange, testName = '') => {
  // ถ้าไม่มีค่า
  if (value === null || value === undefined || value === '') {
    return {
      title: 'ไม่พบข้อมูล',
      color: 'gray',
      caption: 'ไม่พบข้อมูลนี้ในฐานข้อมูล',
      suggest: ''
    };
  }

  // ถ้าไม่มีค่าปกติ
  if (!normalRange || normalRange === '-' || normalRange === 'xxx') {
    return {
      title: 'ไม่พบข้อมูล',
      color: 'gray',
      caption: 'ไม่พบค่าอ้างอิง',
      suggest: ''
    };
  }

  // กรณีค่าเป็น string (เช่น "Negative", "Clear")
  if (typeof value === 'string' && isNaN(parseFloat(value))) {
    // เปรียบเทียบแบบ string
    const normalizedValue = value.toLowerCase().trim();
    const normalizedRange = normalRange.toLowerCase().trim();

    if (normalizedValue === normalizedRange || normalizedRange.includes(normalizedValue)) {
      return {
        title: `${testName}ปกติ`,
        color: 'green',
        caption: `${testName}อยู่ในเกณฑ์ปกติ`,
        suggest: ''
      };
    } else {
      return {
        title: `${testName}ผิดปกติ`,
        color: 'red',
        caption: `${testName}ผิดปกติ`,
        suggest: 'แนะนำให้ปรึกษาแพทย์'
      };
    }
  }

  // กรณีค่าเป็นตัวเลข
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return {
      title: 'ไม่พบข้อมูล',
      color: 'gray',
      caption: 'ข้อมูลไม่ถูกต้อง',
      suggest: ''
    };
  }

  const { min, max } = parseNormalRange(normalRange);

  // ถ้าไม่สามารถ parse ค่าปกติได้
  if (min === null && max === null) {
    return {
      title: 'ไม่พบข้อมูล',
      color: 'gray',
      caption: 'ไม่สามารถประมวลผลค่าอ้างอิงได้',
      suggest: ''
    };
  }

  // เปรียบเทียบค่า
  const isNormal = (min === null || numValue >= min) && (max === null || numValue <= max);

  if (isNormal) {
    return {
      title: `${testName}ปกติ`,
      color: 'green',
      caption: `${testName}อยู่ในเกณฑ์ปกติ (${normalRange})`,
      suggest: ''
    };
  } else {
    const deviation = numValue < min ? 'ต่ำกว่าปกติ' : 'สูงกว่าปกติ';
    return {
      title: `${testName}ผิดปกติ`,
      color: 'red',
      caption: `${testName}${deviation} (ค่าปกติ: ${normalRange})`,
      suggest: 'แนะนำให้ปรึกษาแพทย์'
    };
  }
};

export default {
  getNormalRange,
  getNormalRanges,
  parseNormalRange,
  compareWithNormalRange
};
